"use client"

import { ArrowTurnDownRightIcon } from "@heroicons/react/20/solid"
import { ArrowLeftCircleIcon, ArrowRightCircleIcon } from "@heroicons/react/24/outline"
import Link from "next/link"
import { ReactNode, useCallback, useEffect, useRef, useState } from "react"

import { CourseSeries, CourseWithMeta, sortSourcesRecordsByDate } from "../types"

export interface Props<T extends CourseWithMeta<any>> {
    title: string
    href: string
    courses: Record<string, T>
    template: (slug: string, course: T, priority?: boolean) => ReactNode
    description?: string
    priority?: boolean
    sorter?: (a: T, b: T) => number
}

export function Carousel<T extends CourseWithMeta<any>>({
    description,
    href,
    courses,
    template,
    title,
    priority = false,
}: Props<T>) {
    const containerRef = useRef<HTMLDivElement>(null)
    const [canScrollLeft, setCanScrollLeft] = useState(false)
    const [canScrollRight, setCanScrollRight] = useState(false)

    const updateScrollState = useCallback(() => {
        const node = containerRef.current
        if (!node) {
            setCanScrollLeft(false)
            setCanScrollRight(false)
            return
        }

        const { scrollLeft, scrollWidth, clientWidth } = node
        setCanScrollLeft(scrollLeft > 0)
        setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1)
    }, [])

    const scrollLeft = useCallback(() => {
        containerRef.current?.scrollBy({ left: -300, behavior: "smooth" })
        requestAnimationFrame(updateScrollState)
    }, [updateScrollState])

    const scrollRight = useCallback(() => {
        containerRef.current?.scrollBy({ left: 300, behavior: "smooth" })
        requestAnimationFrame(updateScrollState)
    }, [updateScrollState])

    useEffect(() => {
        const node = containerRef.current
        updateScrollState()

        if (!node) {
            return
        }

        node.addEventListener("scroll", updateScrollState, { passive: true })
        window.addEventListener("resize", updateScrollState)

        return () => {
            node.removeEventListener("scroll", updateScrollState)
            window.removeEventListener("resize", updateScrollState)
        }
    }, [updateScrollState])

    return (
        <section>
            <div className="flex flex-row">
                <h2 className="mb-2 text-xl font-semibold lg:text-2xl dark:text-gray-100">
                    <Link href={href}>{title}</Link>
                </h2>
                <div className="mr-auto ml-5 flex space-x-2 justify-self-start">
                    <button
                        type="button"
                        onClick={scrollLeft}
                        className="group h-10 cursor-pointer rounded-full text-primary-700 hover:text-primary-900 disabled:cursor-not-allowed disabled:text-gray-300 dark:hover:text-primary-600 dark:disabled:text-gray-600"
                        aria-label="Scroll carousel left"
                        disabled={!canScrollLeft}
                    >
                        <ArrowLeftCircleIcon className="size-8 group-hover:scale-110" />
                        <span className="sr-only">Left</span>
                    </button>
                    <button
                        type="button"
                        onClick={scrollRight}
                        className="group h-10 cursor-pointer rounded-full text-primary-700 hover:text-primary-900 disabled:cursor-not-allowed disabled:text-gray-300 dark:hover:text-primary-600 dark:disabled:text-gray-600"
                        aria-label="Scroll carousel right"
                        disabled={!canScrollRight}
                    >
                        <ArrowRightCircleIcon className="size-8 group-hover:scale-110" />
                        <span className="sr-only">Right</span>
                    </button>
                </div>
            </div>
            <p className="mb-5 text-gray-700 dark:text-gray-300">{description}</p>
            <div
                className="flex snap-x gap-4 overflow-x-scroll pb-4"
                style={{ scrollbarWidth: "none" }}
                ref={containerRef}
            >
                {(Object.entries(sortSourcesRecordsByDate(courses)) as Array<[string, T]>).map(
                    ([slug, metadata], index) => template(slug as CourseSeries, metadata, priority && index <= 3)
                )}
                <div className="inline-block w-24 shrink-0 snap-start self-stretch">
                    <Link
                        href={href}
                        className="bg-primary-700 hover:bg-primary-800 dark:hover:text-primary-600 flex h-full flex-col items-center justify-center gap-y-2 rounded font-bold text-white"
                    >
                        <span>View more</span>
                        <ArrowTurnDownRightIcon className="size-8" />
                    </Link>
                </div>
            </div>
        </section>
    )
}
